import { useCallback, useEffect, useRef, useState } from 'react'
import { applyLineItemFieldChange } from '@/utils/line-item-edit'

const addMissingIDs = (items: any[]) =>
    items.map((x: any, i: number) => (x.ID ? x : { ...x, ID: crypto.randomUUID() || 'item' + i }))

const shallowEqualLists = (a: any[], b: any[]) => a.length === b.length && a.every((x, i) => x === b[i])

/**
 * Owns the line item list of a quote/invoice table. Mutators compute from a ref holding the
 * latest items, update state and call `onCommit` synchronously. A new `sourceItems` reference
 * that is shallow-equal to the current items is the parent echoing our commit and is ignored.
 */
export function useLineItems(sourceItems: any[], onCommit: (items: any[]) => void, options: { costing: boolean }) {
    const [items, setItems] = useState<any[]>(() => addMissingIDs(sourceItems ?? []))
    const [savingItem, setSavingItem] = useState(false)
    const itemsRef = useRef(items)
    const onCommitRef = useRef(onCommit)
    const costingRef = useRef(options.costing)
    onCommitRef.current = onCommit
    costingRef.current = options.costing

    const lastSource = useRef(sourceItems)
    useEffect(() => {
        if (lastSource.current === sourceItems) return // mount (already initialised) or unchanged
        lastSource.current = sourceItems
        if (!sourceItems) return
        if (!shallowEqualLists(sourceItems, itemsRef.current)) {
            const next = addMissingIDs(sourceItems)
            itemsRef.current = next
            setItems(next)
        }
        setSavingItem(false)
    }, [sourceItems])

    const commit = useCallback((next: any[]) => {
        itemsRef.current = next
        setItems(next)
        setSavingItem(true)
        onCommitRef.current(next)
    }, [])

    const updateField = useCallback((key: string, item: any, value: any) => {
        // Derive from the latest item, not the caller's (possibly stale) render copy.
        const current = itemsRef.current.find((x: any) => x.ID === item.ID)
        if (!current) return // row was removed before this late edit arrived; nothing to commit
        const newItem = applyLineItemFieldChange(current, key, value, { costing: costingRef.current })
        commit(itemsRef.current.map((x: any) => (x.ID === item.ID ? newItem : x)))
    }, [commit])

    const removeItem = useCallback((id: string) => {
        commit(itemsRef.current.filter((x: any) => x.ID !== id))
    }, [commit])

    const removeSection = useCallback((sectionId: string) => {
        commit(
            itemsRef.current
                .filter((x: any) => x.InventorySectionID !== sectionId)
                .map((x: any, i: number) => ({ ...x, LineNumber: i + 1 }))
        )
    }, [commit])

    return { items, savingItem, updateField, commit, removeItem, removeSection }
}

export default useLineItems
